import { useEffect, useRef, useState } from 'react';

import { Box, Button, Heading, HStack, Portal, Stack, useColorModeValue } from '@ecoinvent/ui';

import { TourContextValue, TourPlacement, TourStep } from '../types';

import StepIndicator from './StepIndicator';

interface CoachmarkProps {
  stepIndex: number;
  steps: TourStep[];
  useTourHook: () => TourContextValue;
}

interface Position {
  top: number;
  left: number;
}

const ARROW_SIZE = 12;
const OFFSET = 16;
const SCREEN_PADDING = 16;

const getPopoverPosition = (
  targetRect: DOMRect,
  popoverRect: DOMRect,
  placement: TourPlacement
): { position: Position; arrowPosition: Position; arrowRotation: string } => {
  let position: Position;
  let arrowPosition: Position;
  let arrowRotation: string;

  const targetCenterX = targetRect.left + targetRect.width / 2;

  switch (placement) {
    case 'right':
      position = {
        top: targetRect.top + targetRect.height / 2 - popoverRect.height / 2,
        left: targetRect.right + OFFSET,
      };
      arrowPosition = {
        top: popoverRect.height / 2 - ARROW_SIZE / 2,
        left: -ARROW_SIZE,
      };
      arrowRotation = '-90deg';
      break;
    case 'left':
      position = {
        top: targetRect.top + targetRect.height / 2 - popoverRect.height / 2,
        left: targetRect.left - popoverRect.width - OFFSET,
      };
      arrowPosition = {
        top: popoverRect.height / 2 - ARROW_SIZE / 2,
        left: popoverRect.width - ARROW_SIZE / 2,
      };
      arrowRotation = '90deg';
      break;
    case 'bottom': {
      const idealLeft = targetRect.left + targetRect.width / 2 - popoverRect.width / 2;

      const minLeft = SCREEN_PADDING;
      const maxLeft = window.innerWidth - popoverRect.width - SCREEN_PADDING;
      const clampedLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));

      position = {
        top: targetRect.bottom + OFFSET,
        left: clampedLeft,
      };

      const arrowLeft = targetCenterX - clampedLeft - ARROW_SIZE / 2;
      arrowPosition = {
        top: -ARROW_SIZE,
        left: Math.max(ARROW_SIZE, Math.min(arrowLeft, popoverRect.width - ARROW_SIZE * 2)),
      };
      arrowRotation = '0deg';
      break;
    }
    case 'top':
    default: {
      const idealLeft = targetRect.left + targetRect.width / 2 - popoverRect.width / 2;

      const minLeft = SCREEN_PADDING;
      const maxLeft = window.innerWidth - popoverRect.width - SCREEN_PADDING;
      const clampedLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));

      position = {
        top: targetRect.top - popoverRect.height - OFFSET,
        left: clampedLeft,
      };

      const arrowLeft = targetCenterX - clampedLeft - ARROW_SIZE / 2;
      arrowPosition = {
        top: popoverRect.height - ARROW_SIZE / 2,
        left: Math.max(ARROW_SIZE, Math.min(arrowLeft, popoverRect.width - ARROW_SIZE * 2)),
      };
      arrowRotation = '180deg';
      break;
    }
  }

  return { position, arrowPosition, arrowRotation };
};

const Coachmark = ({ stepIndex, steps, useTourHook }: CoachmarkProps) => {
  const { state, nextStep, prevStep, endTour } = useTourHook();
  const step = steps[stepIndex];
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [arrowPosition, setArrowPosition] = useState<Position | null>(null);
  const [arrowRotation, setArrowRotation] = useState('0deg');
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.200');

  const isActive = state.isActive && state.currentStep === stepIndex;
  const isLastStep = stepIndex === steps.length - 1;

  // Count only coachmark steps for the indicator
  const coachmarkSteps = steps.filter((s) => s.type === 'coachmark');
  const coachmarkIndex = coachmarkSteps.findIndex((s) => s.id === step.id);
  const isFirstCoachmark = coachmarkIndex === 0;

  useEffect(() => {
    if (!isActive || !step.targetId) {
      setPosition(null);
      return;
    }

    let retryCount = 0;
    const maxRetries = 20;
    let retryTimeoutId: NodeJS.Timeout;

    const updatePosition = () => {
      const targetElement = document.querySelector(`[data-tour-target="${step.targetId}"]`);
      const popoverElement = popoverRef.current;

      if (targetElement && popoverElement) {
        const targetRect = targetElement.getBoundingClientRect();
        const popoverRect = popoverElement.getBoundingClientRect();
        const placement = step.placement || 'bottom';

        const {
          position: newPosition,
          arrowPosition: newArrowPosition,
          arrowRotation: newRotation,
        } = getPopoverPosition(targetRect, popoverRect, placement);

        setPosition(newPosition);
        setArrowPosition(newArrowPosition);
        setArrowRotation(newRotation);
      } else if (retryCount < maxRetries) {
        // Element not found yet, retry after a delay
        retryCount++;
        retryTimeoutId = setTimeout(updatePosition, 300);
      }
    };

    const timeoutId = setTimeout(updatePosition, 100);

    const recalcIntervalId = setInterval(updatePosition, 200);
    const stopRecalcTimeoutId = setTimeout(() => {
      clearInterval(recalcIntervalId);
    }, 1500);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
      clearInterval(recalcIntervalId);
      clearTimeout(stopRecalcTimeoutId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isActive, step.targetId, step.placement]);

  const headingId = `coachmark-heading-${step.id}`;
  const contentId = `coachmark-content-${step.id}`;

  if (!isActive || step.type !== 'coachmark') {
    return null;
  }

  return (
    <Portal>
      <Box
        ref={popoverRef}
        role="dialog"
        aria-labelledby={headingId}
        aria-describedby={contentId}
        aria-modal="true"
        position="fixed"
        top={position?.top ?? -9999}
        left={position?.left ?? -9999}
        zIndex="popover"
        bg={bgColor}
        p={5}
        borderRadius="lg"
        maxW="320px"
        boxShadow="xl"
        borderWidth="0.5px"
        borderColor={borderColor}
        opacity={position ? 1 : 0}
        transition="opacity 0.2s"
      >
        {arrowPosition && (
          <Box
            position="absolute"
            top={`${arrowPosition.top}px`}
            left={`${arrowPosition.left}px`}
            width={0}
            height={0}
            borderLeft={`${ARROW_SIZE}px solid transparent`}
            borderRight={`${ARROW_SIZE}px solid transparent`}
            borderBottom={`${ARROW_SIZE}px solid`}
            borderBottomColor={bgColor}
            transform={`rotate(${arrowRotation})`}
          />
        )}

        <Stack spacing={3}>
          <Heading id={headingId} size="sm">
            {step.title}
          </Heading>
          <Box id={contentId} fontSize="sm">
            {step.content}
          </Box>

          <HStack justify="space-between">
            {!isFirstCoachmark ? (
              <>
                <Button variant="outline" colorScheme="gray" size="sm" onClick={prevStep}>
                  Back
                </Button>
                <StepIndicator totalSteps={coachmarkSteps.length} currentStep={coachmarkIndex} />
              </>
            ) : (
              <StepIndicator totalSteps={coachmarkSteps.length} currentStep={coachmarkIndex} />
            )}
            <Button variant="solid" size="sm" onClick={isLastStep ? endTour : nextStep}>
              {isLastStep ? 'Finish' : 'Next'}
            </Button>
          </HStack>
        </Stack>
      </Box>
    </Portal>
  );
};

export default Coachmark;
